import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, ArrowRight, Globe } from 'lucide-react';
import type { Client } from '@/types/client';

interface ClientSelectorProps {
  clients: Client[];
  onClientSelect: (clientSlug: string) => void;
}

export function ClientSelector({ clients, onClientSelect }: ClientSelectorProps) {
  const navigate = useNavigate();

  const handleClientSelect = (client: Client) => {
    onClientSelect(client.slug);
    navigate(`/dashboard/${client.slug}`);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted/50 p-4">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Sélectionner un client</h1>
          <p className="text-muted-foreground mt-2">
            Choisissez le client pour lequel vous souhaitez consulter le dashboard
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {clients.map((client) => (
            <Card key={client.id} className="cursor-pointer transition-all hover:shadow-lg hover:scale-105">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {client.brand_logo_url ? (
                      <img
                        src={client.brand_logo_url}
                        alt={client.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" />
                      </div>
                    )}
                    <div className="flex-1">
                      <CardTitle className="text-lg">{client.name}</CardTitle>
                      <CardDescription className="text-sm">
                        @{client.slug}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="pt-0">
                <div className="space-y-3">
                  {/* Marketplaces */}
                  <div className="flex flex-wrap gap-1">
                    {client.marketplaces.map((marketplace) => (
                      <Badge key={marketplace} variant="secondary" className="text-xs">
                        <Globe className="w-3 h-3 mr-1" />
                        {marketplace.replace('amazon.', '').toUpperCase()}
                      </Badge>
                    ))}
                  </div>

                  <Button
                    className="w-full"
                    onClick={() => handleClientSelect(client)}
                  >
                    Accéder au dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {clients.length === 0 && (
          <Card className="text-center p-8">
            <CardContent>
              <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <CardTitle className="mb-2">Aucun client disponible</CardTitle>
              <CardDescription>
                Vous n'avez accès à aucun client pour le moment. 
                Contactez votre administrateur pour obtenir les accès nécessaires.
              </CardDescription>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}